import {
  INITIAL_DAILY_SEGMENTS,
  INITIAL_SCRATCH_SEGMENTS,
  INITIAL_SEGMENTS,
  SEGMENT_COLORS,
} from '../wheel/data.js';
import type { WheelSegment } from '../wheel/types.js';
import { progressionWithDefaults, seedProgression } from './progression.js';
import type {
  CreditPack,
  CreditTransaction,
  EconomySettings,
  EconomyState,
  WheelConfig,
} from './types.js';

/**
 * The shape a brand-new guild starts from, and the backfill that lets an older
 * save file load into a newer build.
 *
 * This lives beside the types on purpose: **adding a field to `EconomyState`
 * means adding it to `withDefaults` too**, or every existing guild fails to load.
 */

/**
 * A new player is worth nothing until someone decides otherwise.
 *
 * Zero, because credits are a loyalty reward rather than a product: a client is
 * granted an amount matching what he has already spent, by hand. It was 1000
 * back when a single demo guild wanted something to play with, and that number
 * became wrong the moment any server could open a realm — every arrival in every
 * new server would have been handed nine free spins on the creator's wheel.
 *
 * An existing save carries its own value and keeps it: `withDefaults` only
 * substitutes this where the field was never written.
 */
export const DEFAULT_STARTING_CREDITS = 0;

/**
 * **The one rate: 20 credits — and 20 XP — per dollar.**
 *
 * The reward catalogue, the XP scale, the status ladders and the server-wide
 * ladder's USD thresholds all ride this number, so it lives here once. It used to
 * exist only as prose in comments; anything converting between dollars and
 * credits must go through it rather than writing a second 20.
 */
export const CREDITS_PER_DOLLAR = 20;

/** Dollars → the credit-equivalent units spend and thresholds are counted in. */
export function usdToCredits(usd: number): number {
  return Math.round(usd * CREDITS_PER_DOLLAR);
}

/** The inverse, to two decimals — for the owner's own display only. */
export function creditsToUsd(credits: number): number {
  return Math.round((credits / CREDITS_PER_DOLLAR) * 100) / 100;
}

/** Economy-wide defaults, in one place so `seedState` and `withDefaults` agree. */
export const DEFAULT_SETTINGS: EconomySettings = {
  startingCredits: DEFAULT_STARTING_CREDITS,
  /** Coupons stack; without a ceiling a big enough stack makes a pack free. */
  maxCouponPercent: 50,
  currency: 'USD',
  maxOpenOrders: 2,
  // The house names. A creator renames them from her console; these are what a
  // profile reads until she does, and what every save from before them reads.
  creditName: 'Credit',
  creditNamePlural: 'Credits',
  xpName: 'XP',
};

/**
 * Starting reward tiers.
 *
 * **One rate, everywhere: 20 credits per dollar.** The brackets exist only so a
 * player can pick the one matching what they did — the ratio never changes
 * between them, so there is no tier worth farming and no arbitrage to police.
 * Tying a reward to an *action* instead ("subscribe", "buy any content") is
 * what makes a catalogue drift: the same action can be worth $3 or $50.
 *
 * `requirement` is what the player reads, and it is deliberately neutral about
 * who the creator is — this app should outlive any one of them. `price` is the
 * owner's private note of what a tier is worth and never reaches a player.
 */
export const INITIAL_PACKS: CreditPack[] = [
  {
    id: 'p1',
    name: 'Supporter',
    credits: 200,
    requirement: "Spend $10 on the creator's page",
    price: 10,
    tagline: 'Two Premium spins',
    featured: false,
    active: true,
  },
  {
    id: 'p2',
    name: 'Fan',
    credits: 500,
    requirement: "Spend $25 on the creator's page",
    price: 25,
    tagline: 'Five spins',
    featured: true,
    active: true,
  },
  {
    id: 'p3',
    name: 'VIP',
    credits: 1000,
    requirement: "Spend $50 on the creator's page",
    price: 50,
    tagline: 'Ten spins',
    featured: false,
    active: true,
  },
  {
    id: 'p4',
    name: 'Elite',
    credits: 2000,
    requirement: "Spend $100 on the creator's page",
    price: 100,
    tagline: 'Twenty spins',
    featured: false,
    active: true,
  },
];

/**
 * A fresh economy: the two stock wheels, and **no players**.
 *
 * Accounts are created from real Discord identities the first time someone opens
 * the activity. Seeding fake ones here is what used to make the owner console
 * show a roster nobody could recognise.
 */
export function seedState(): EconomyState {
  return {
    players: [],
    transactions: [],
    spins: [],
    wheels: {
      main: {
        name: 'Lucky Drop',
        description: 'Spend credits for a shot at the big prizes.',
        enabled: true,
        spinCost: 10,
        cooldownHours: 0,
        sortByDisplay: false,
        segments: INITIAL_SEGMENTS,
      },
      // Still keyed `daily` — the id is written into every save file, so it is
      // not worth renaming for a label the owner can already change.
      daily: {
        name: 'Premium Drop',
        description: 'The big one. Better prizes, higher stakes.',
        enabled: true,
        spinCost: 100,
        cooldownHours: 0,
        sortByDisplay: false,
        segments: INITIAL_DAILY_SEGMENTS,
      },
      // **Off until the owner turns it on.** A new draw must not appear in every
      // existing realm the day it ships, and a stranger's realm should not start
      // with a card it never set a price on. Everything else about it is the same
      // shape as the two wheels.
      scratch: {
        name: 'Scratch Card',
        description: 'Scratch to reveal a prize.',
        enabled: false,
        spinCost: 20,
        cooldownHours: 0,
        sortByDisplay: false,
        segments: INITIAL_SCRATCH_SEGMENTS,
      },
    },
    lastSpins: { main: {}, daily: {}, scratch: {} },
    shop: INITIAL_PACKS,
    orders: [],
    coupons: [],
    progression: seedProgression(),
    settings: { ...DEFAULT_SETTINGS },
  };
}

/**
 * How much of an account's history counts as purchase spend, for a save that
 * predates the `source` tag.
 *
 * Read off the ledger, because that is all an old save has. Delivered claims
 * ("Reward: …") are purchases by construction. The daily claim and tier rewards
 * were bonuses and are excluded by their reasons. Everything else that was a
 * plain `grant` is an owner grant, and under the rule that credits are granted
 * by hand to match what a client spent, it counts — the alternative is a ladder
 * that starts every existing client at the bottom on the day it ships.
 *
 * Status the owner *seeded* by hand left no ledger line and cannot be recovered
 * here; the console's XP grant now records it as spend going forward.
 */
function legacySpend(transactions: CreditTransaction[], playerId: string): number {
  let spend = 0;
  for (const tx of transactions) {
    if (tx.playerId !== playerId || tx.type !== 'grant') continue;
    if (tx.source === 'bonus') continue;
    if (tx.source !== 'purchase') {
      const bonus = /^Daily claim/i.test(tx.reason) || /status reward$/i.test(tx.reason);
      if (bonus) continue;
    }
    spend += tx.amount;
  }
  return spend;
}

/** A persisted segment from before the odds were split into real/display. */
type PersistedSegment = Partial<WheelSegment> & { weight?: number };

/**
 * Backfills segments persisted before `weight` became `realChance` +
 * `displayChance`.
 *
 * The old single number was the draw weight, so it becomes the real chance
 * untouched, and the display chance is seeded from its normalised share. A
 * returning player therefore sees exactly the odds they saw yesterday, and the
 * owner starts from an honest wheel they can then diverge on purpose.
 */
function segmentsWithDefaults(
  parsed: PersistedSegment[] | undefined,
  fallback: WheelSegment[],
): WheelSegment[] {
  if (!parsed || parsed.length === 0) return fallback;

  const total = parsed.reduce((sum, raw) => sum + (raw.realChance ?? raw.weight ?? 0), 0);

  return parsed.map((raw, index) => {
    const realChance = raw.realChance ?? raw.weight ?? 0;
    // The `xp` prize type is retired: nothing in the game grants status any more.
    // A slice saved as `xp` becomes a `credits` slice of the same amount, so a
    // returning player still wins something real instead of a prize that pays
    // nothing — and it grants no XP and no rank, like every other wheel credit.
    const reward = raw.reward === 'xp' ? 'credits' : (raw.reward ?? 'nothing');
    return {
      id: raw.id ?? `s${index}`,
      name: raw.name ?? 'Reward',
      reward,
      value: raw.value ?? 0,
      realChance,
      displayChance:
        raw.displayChance ?? (total > 0 ? Math.round((realChance / total) * 1000) / 10 : 0),
      color: raw.color ?? SEGMENT_COLORS[0],
      icon: raw.icon ?? 'gift',
      active: raw.active ?? true,
    };
  });
}

/** Backfills a wheel config missing fields from an older persisted state. */
function wheelWithDefaults(
  parsed: Partial<WheelConfig> | undefined,
  fallback: WheelConfig,
): WheelConfig {
  return {
    name: parsed?.name ?? fallback.name,
    description: parsed?.description ?? fallback.description,
    enabled: parsed?.enabled ?? fallback.enabled,
    spinCost: parsed?.spinCost ?? fallback.spinCost,
    cooldownHours: parsed?.cooldownHours ?? fallback.cooldownHours,
    sortByDisplay: parsed?.sortByDisplay === true,
    segments: segmentsWithDefaults(parsed?.segments, fallback.segments),
  };
}

/** Backfills fields missing from an older persisted state so upgrades are safe. */
export function withDefaults(parsed: Partial<EconomyState>): EconomyState {
  const seed = seedState();
  return {
    // `xp` and `anonymous` arrived with the status ladder; every player from
    // before it starts at the bottom rung with their name showing.
    players: (parsed.players ?? seed.players).map((player) => ({
      ...player,
      xp: typeof player.xp === 'number' && Number.isFinite(player.xp) ? player.xp : 0,
      // Arrived with the server-wide ladder. A save without it is derived once
      // from its ledger; after that the field is written and this is not read.
      lifetimeSpend:
        typeof player.lifetimeSpend === 'number' && Number.isFinite(player.lifetimeSpend)
          ? Math.max(0, player.lifetimeSpend)
          : legacySpend(parsed.transactions ?? [], player.id),
      anonymous: player.anonymous === true,
      // Engagement analytics arrived after these players did; everyone from
      // before starts blank and accumulates from their next heartbeat.
      timeSpentSeconds:
        typeof player.timeSpentSeconds === 'number' && Number.isFinite(player.timeSpentSeconds)
          ? player.timeSpentSeconds
          : 0,
      sessionCount:
        typeof player.sessionCount === 'number' && Number.isFinite(player.sessionCount)
          ? player.sessionCount
          : 0,
      lastSeen: typeof player.lastSeen === 'string' ? player.lastSeen : null,
      firstSeen: typeof player.firstSeen === 'string' ? player.firstSeen : null,
    })),
    transactions: parsed.transactions ?? [],
    spins: parsed.spins ?? [],
    wheels: {
      main: wheelWithDefaults(parsed.wheels?.main, seed.wheels.main),
      daily: wheelWithDefaults(parsed.wheels?.daily, seed.wheels.daily),
      // A save from before the scratch card has none: it gets the default one,
      // switched off, so an existing realm is exactly as it was.
      scratch: wheelWithDefaults(parsed.wheels?.scratch, seed.wheels.scratch),
    },
    lastSpins: {
      main: parsed.lastSpins?.main ?? {},
      daily: parsed.lastSpins?.daily ?? {},
      scratch: parsed.lastSpins?.scratch ?? {},
    },
    // `??` and not `||`: an owner who deleted every tier means an empty
    // catalogue, and must not be handed the seed one back on the next restart.
    //
    // `requirement` arrived when credits stopped being sold and became a
    // loyalty reward. A tier saved before that has none, and an empty string is
    // the right answer — the owner writes what earns it, nobody can guess.
    shop: (parsed.shop ?? seed.shop).map((pack) => ({
      ...pack,
      requirement: pack.requirement ?? '',
    })),
    orders: parsed.orders ?? [],
    coupons: parsed.coupons ?? [],
    // `lastClaims`, `streaks`, `streak` and the `dailyClaim*` settings belonged to
    // the daily claim, which credits-as-loyalty made contradictory and which is
    // gone. An older save may still carry them: they are simply not read, so the
    // save loads unchanged and the next write drops them. Balances, XP, coupons
    // and the ledger — including past "Daily claim" lines — are untouched.
    progression: progressionWithDefaults(parsed.progression),
    settings: {
      startingCredits: parsed.settings?.startingCredits ?? seed.settings.startingCredits,
      maxCouponPercent: parsed.settings?.maxCouponPercent ?? seed.settings.maxCouponPercent,
      currency: parsed.settings?.currency ?? seed.settings.currency,
      maxOpenOrders: parsed.settings?.maxOpenOrders ?? seed.settings.maxOpenOrders,
      // Empty strings fall back too, not just missing ones: a name cleared in
      // the console must read "Credit" rather than leave the UI with a blank
      // where the currency goes.
      creditName: parsed.settings?.creditName || seed.settings.creditName,
      creditNamePlural: parsed.settings?.creditNamePlural || seed.settings.creditNamePlural,
      xpName: parsed.settings?.xpName || seed.settings.xpName,
    },
  };
}
